import React from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const GuestPosts = () => {
  const guestPosts = [
    {
      id: 1,
      title: "From Bootcamp to Senior Developer: My Journey with Adrian\'s Mentorship",
      excerpt: `How Adrian's guidance transformed my career from a struggling bootcamp graduate to a confident senior developer at a Fortune 500 company.\n\nThis is my personal story of growth, challenges overcome, and the mentorship approach that made all the difference.`,
      author: {
        name: "Nkechi Okoye",
        role: "Senior Frontend Developer at Microsoft",
        avatar: "https://placehold.co/100x100",
        linkedin: "https://linkedin.com/in/nkechiokoye"
      },
      publishedAt: "2025-01-02",
      readTime: "9 min read",
      category: "Career Growth",
      tags: ["Mentorship", "Career", "Success Story"],
      image: "https://placehold.co/600x400",
      views: 3200,
      likes: 89,
      featured: true
    },
    {
      id: 2,
      title: "Building My First Open Source Project: Lessons from CodersHub",
      excerpt: "The story of creating my first meaningful open source contribution and how the CodersHub community supported my journey.",
      author: {
        name: "Kwame Nkrumah",
        role: "Full Stack Developer",
        avatar: "https://placehold.co/100x100",
        linkedin: "https://linkedin.com/in/kwamenkrumah"
      },
      publishedAt: "2024-12-28",
      readTime: "7 min read",
      category: "Community Building",
      tags: ["Open Source", "Community", "Learning"],
      image: "https://placehold.co/600x400",
      views: 2100,
      likes: 67,
      featured: false
    },
    {
      id: 3,
      title: "Transitioning from Backend to Full Stack: A Mentee\'s Perspective",
      excerpt: "My experience expanding from backend development to full stack under Adrian\'s guidance and the CodersHub community support.",
      author: {
        name: "Aisha Mohammed",
        role: "Full Stack Developer at Shopify",
        avatar: "https://placehold.co/100x100",
        linkedin: "https://linkedin.com/in/aishamohammed"
      },
      publishedAt: "2024-12-20",
      readTime: "11 min read",
      category: "Career Growth",
      tags: ["Full Stack", "Transition", "Mentorship"],
      image: "https://placehold.co/600x400",
      views: 2800,
      likes: 74,
      featured: false
    },
    {
      id: 4,
      title: "How CodersHub Changed My Approach to Learning React",
      excerpt: "The collaborative learning environment at CodersHub transformed how I approach mastering new technologies.",
      author: {
        name: "David Kim",
        role: "React Developer at Airbnb",
        avatar: "https://placehold.co/100x100",
        linkedin: "https://linkedin.com/in/davidkim"
      },
      publishedAt: "2024-12-15",
      readTime: "6 min read",
      category: "Technical Tutorials",
      tags: ["React", "Learning", "Community"],
      image: "https://placehold.co/600x400",
      views: 1900,
      likes: 52,
      featured: false
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'technical tutorials':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'career growth':
        return 'bg-success/10 text-success border-success/20';
      case 'community building':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  return (
    <section className="bg-background py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          {/* Section Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-2">
              <Icon name="Users" size={24} className="text-primary" />
              <h2 className="text-3xl font-brand-headline text-foreground">
                Community Voices
              </h2>
            </div>
            <p className="text-lg text-text-secondary font-brand-body max-w-3xl mx-auto">
              Stories, insights, and experiences shared by our amazing community members and mentees. 
              Their journeys inspire and guide fellow developers on their own paths to success.
            </p>
          </div>

          {/* Featured Guest Post */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden brand-shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Image */}
              <div className="relative h-64 lg:h-auto">
                <Image
                  src={guestPosts?.[0]?.image}
                  alt={guestPosts?.[0]?.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Featured Badge */}
                <div className="absolute top-4 left-4">
                  <span className="flex items-center space-x-1 px-3 py-1 bg-primary/90 backdrop-blur-sm text-primary-foreground rounded-full text-sm font-brand-medium">
                    <Icon name="Star" size={14} />
                    <span>Featured Story</span>
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="lg:col-span-2 p-8 lg:p-12 space-y-6">
                {/* Author Info */}
                <div className="flex items-center space-x-4">
                  <Image
                    src={guestPosts?.[0]?.author?.avatar}
                    alt={guestPosts?.[0]?.author?.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="space-y-1">
                    <h4 className="font-brand-subheading text-foreground">
                      {guestPosts?.[0]?.author?.name}
                    </h4>
                    <p className="text-sm text-text-secondary font-brand-body">
                      {guestPosts?.[0]?.author?.role}
                    </p>
                  </div>
                  <a
                    href={guestPosts?.[0]?.author?.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto p-2 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-brand"
                    aria-label="LinkedIn Profile"
                  >
                    <Icon name="Linkedin" size={18} />
                  </a>
                </div>

                {/* Post Meta */}
                <div className="flex items-center space-x-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-brand-medium border ${getCategoryColor(guestPosts?.[0]?.category)}`}>
                    {guestPosts?.[0]?.category}
                  </span>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="Calendar" size={14} />
                    <span>{formatDate(guestPosts?.[0]?.publishedAt)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="Clock" size={14} />
                    <span>{guestPosts?.[0]?.readTime}</span>
                  </div>
                </div>

                {/* Title & Excerpt */}
                <div className="space-y-3">
                  <h3 className="text-xl lg:text-2xl font-brand-headline text-foreground leading-tight">
                    {guestPosts?.[0]?.title}
                  </h3>
                  <p className="text-text-secondary font-brand-body">
                    {guestPosts?.[0]?.excerpt?.split('\n')?.[0]}
                  </p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {guestPosts?.[0]?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-text-secondary rounded-md text-xs font-brand-medium hover:bg-primary/10 hover:text-primary transition-brand cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Stats & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={14} />
                      <span>{guestPosts?.[0]?.views?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Heart" size={14} />
                      <span>{guestPosts?.[0]?.likes}</span>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={16}
                    className="border-primary/20 text-primary hover:bg-primary/10"
                  >
                    Read Story
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Other Guest Posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guestPosts?.slice(1)?.map((post) => (
              <article
                key={post?.id}
                className="bg-card rounded-xl border border-border overflow-hidden hover:brand-shadow transition-all duration-300 group"
              >
                {/* Post Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={post?.image}
                    alt={post?.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  
                  {/* Author Avatar Overlay */}
                  <div className="absolute bottom-3 left-3">
                    <Image
                      src={post?.author?.avatar}
                      alt={post?.author?.name}
                      className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    />
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-6 space-y-4">
                  {/* Author & Meta */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-brand-subheading text-foreground">
                        {post?.author?.name}
                      </h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-brand-medium border ${getCategoryColor(post?.category)}`}>
                        {post?.category}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary font-brand-body">
                      {post?.author?.role}
                    </p>
                  </div>

                  {/* Title */}
                  <h4 className="text-base font-brand-subheading text-foreground leading-tight line-clamp-2 group-hover:text-primary transition-brand">
                    {post?.title}
                  </h4>

                  {/* Excerpt */}
                  <p className="text-text-secondary font-brand-body text-sm line-clamp-2">
                    {post?.excerpt}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center space-x-3 text-xs text-text-secondary">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} />
                        <span>{formatDate(post?.publishedAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} />
                        <span>{post?.readTime}</span>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="xs"
                      iconName="ArrowRight"
                      iconPosition="right"
                      iconSize={14}
                      className="text-text-secondary hover:text-primary group-hover:translate-x-1 transition-all duration-300"
                    >
                      Read
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-brand rounded-2xl p-8 lg:p-12 text-center space-y-6">
            <div className="space-y-4">
              <h3 className="text-2xl lg:text-3xl font-brand-headline text-white">
                Share Your Story
              </h3>
              <p className="text-lg text-white/80 font-brand-body max-w-2xl mx-auto">
                Have an inspiring journey or valuable insights to share with the community? 
                We'd love to feature your story and help others learn from your experience.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                iconName="PenTool"
                iconPosition="left"
                iconSize={18}
                className="bg-primary text-primary-foreground hover:bg-primary/90 brand-shadow"
              >
                Submit Your Story
              </Button>
              <Button
                variant="outline"
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={18}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Join Community
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GuestPosts;